import React from "react";
import { Inbox } from "lucide-react";

export type DashboardColumnKind = "text" | "number" | "duration";

interface DashboardTableProps {
  title: string;
  headers: string[];
  data: string[][];
  icon?: React.ReactNode;
  /** Alinhamento e tipografia por coluna (mesma ordem que headers) */
  columnKinds?: DashboardColumnKind[];
}

function headerCellClass(kind: DashboardColumnKind | undefined): string {
  const base =
    "px-4 py-3 text-xs font-medium uppercase tracking-wider text-neutral-500 sm:px-6";
  if (kind === "number" || kind === "duration") {
    return `${base} text-right`;
  }
  return `${base} text-left`;
}

function bodyCellClass(kind: DashboardColumnKind | undefined): string {
  const base = "whitespace-nowrap px-4 py-4 text-sm text-neutral-900 sm:px-6";
  if (kind === "number") {
    return `${base} text-right tabular-nums`;
  }
  if (kind === "duration") {
    return `${base} text-right tabular-nums`;
  }
  return `${base} text-left`;
}

const DashboardTable: React.FC<DashboardTableProps> = ({
  title,
  headers,
  data,
  icon,
  columnKinds,
}) => {
  return (
    <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-sm">
      <div className="border-b border-neutral-200 px-4 py-4 sm:px-6">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-neutral-800">
          {icon}
          {title}
        </h2>
      </div>

      {data.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                {headers.map((header, index) => (
                  <th
                    key={index}
                    scope="col"
                    className={headerCellClass(columnKinds?.[index])}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200 bg-white">
              {data.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={
                    rowIndex % 2 === 1
                      ? "bg-neutral-50/70 hover:bg-neutral-100/80"
                      : "hover:bg-neutral-50"
                  }
                >
                  {row.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      className={bodyCellClass(columnKinds?.[cellIndex])}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center text-neutral-500 sm:px-6">
          <Inbox className="h-10 w-10 text-neutral-300" aria-hidden />
          <p className="text-sm font-medium text-neutral-600">
            Nenhum dado encontrado
          </p>
          <p className="max-w-sm text-xs text-neutral-500">
            Tente outro período, outra loja ou aguarde novas movimentações no
            intervalo selecionado.
          </p>
        </div>
      )}
    </div>
  );
};

export default DashboardTable;
